<template>
    <v-row dense id="expenses_page">

        <v-col cols="12" lg="3" v-if="search_enabled">

            <v-card class="custom_card mb-2">
                <v-card-text class="pr-6 pl-6">
                    <div>
                        <v-toolbar-title class="field_color mb-5">{{$t('general.amount_sum')}} : {{amount_sum}} €
                        </v-toolbar-title>
                    </div>
                </v-card-text>
            </v-card>

            <v-expansion-panels v-model="search_panel" :disabled="loading">
                <v-expansion-panel>
                    <v-expansion-panel-header>{{$t('general.search')}}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row dense>
                            <v-col cols="12" lg="12" md="4" sm="6">
                                <v-menu v-model="date_from_menu" :close-on-content-click="false" max-width="290">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field name="date_from" type="text" class="custom_field"
                                                      :value="$Helper.methods.formatDate(search.date_from)"
                                                      :label="$t('general.date_from')" outlined readonly
                                                      :disabled="loading" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="search.date_from" :locale="locale" no-title
                                                   @change="date_from_menu = false">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" lg="12" md="4" sm="6">
                                <v-menu v-model="date_to_menu" :close-on-content-click="false" max-width="290">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field name="date_to" type="text" class="custom_field"
                                                      :value="$Helper.methods.formatDate(search.date_to)"
                                                      :label="$t('general.date_to')" outlined readonly
                                                      :disabled="loading" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="search.date_to" :locale="locale" no-title
                                                   @change="date_to_menu = false">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" lg="12" md="4" sm="6">
                                <v-select name="expense_type_id" class="custom_field"
                                          v-model="search.expense_type_id" :items="expense_types"
                                          :label="$t('expenses.type')" item-text="name" item-value="id" outlined
                                          clearable :disabled="loading">
                                </v-select>
                            </v-col>
                            <v-col cols="12" align="right">
                                <v-btn color="primary" :disabled="loading" @click="clearSearchForm">
                                    {{$t('general.clear')}}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-col>

        <v-col cols="12" :lg="search_enabled? 9 : 12">
            <!--Datatable Toolbar-->
            <v-toolbar flat color="white" elevation="1" class="custom_datatable_toolbar" height="56">
                <v-toolbar-title>{{$t('expenses.list')}}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-if="canCreateItem" color="primary" dark class="custom_button mobile_button"
                       @click="openItemDialog('create', 0)" :title="$t('expenses.create')">
                    <v-icon small size="20">add</v-icon>
                </v-btn>

            </v-toolbar>

            <!--Datatable-->
            <ResponsiveDataTable :headers="headers" :items="items" :totalItems="total_items" :loading="loading"
                                 :mobileBreakpoint="960" :itemsPerPage="500"
                                 :itemsPerPageOptions="[10, 50, 100, 200, 500]" @options-changed="optionsChanged">
                <!--Buttons-->
                <template v-slot:buttons="{item}">
                    <div class="actions">
                        <v-icon v-if="canViewItem" color="primary" :title="$t('general.view')"
                                @click="openItemDialog('view', item.item.id)">view_list
                        </v-icon>
                        <v-icon v-if="$Helper.methods.canUserDoAction(canUpdateItem, isSuperAdmin, item.item)"
                                color="primary" :title="$t('general.update')"
                                @click="openItemDialog('update', item.item.id)">
                            edit
                        </v-icon>
                        <v-icon v-if="$Helper.methods.canUserDoAction(canDeleteItem, isSuperAdmin, item.item)"
                                color="primary" :title="$t('general.delete')" @click="showDeleteItemDialog(item.item)">
                            delete
                        </v-icon>
                    </div>
                </template>
            </ResponsiveDataTable>
        </v-col>

        <!--Item Dialog-->
        <ManageExpenseDialog :show="show_item_dialog" :item_header="item_dialog_header"
                             :is_loading="item_dialog_loading" :item_action="item_action" :item_id="item_id"
                             @close-dialog="closeItemDialog" @submit-dialog="submitItemDialog">
        </ManageExpenseDialog>

        <!--Delete item dialog-->
        <StatusDialog :show="show_delete_item_dialog" :header="$t('expenses.delete')" color="error"
                      :text="$t('expenses.delete_confirmation')" :is_loading="delete_item_dialog_loading"
                      @confirm="deleteItem" @close="closeDeleteItemDialog">
        </StatusDialog>

    </v-row>
</template>

<script>
    import ResponsiveDataTable from '../components/ResponsiveDataTable.vue'
    import ManageExpenseDialog from '../components/ManageExpenseDialog.vue'
    import StatusDialog from '../components/StatusDialog.vue'
    import _ from 'lodash'

    export default {
        props: {
            search_enabled: {type: Boolean, default: false},
            criteria: {type: Object, default: null}
        },
        components: {
            ResponsiveDataTable, StatusDialog, ManageExpenseDialog
        },
        data: self => ({
            url: 'expenses',

            // actions permissions
            canViewItem: false,
            canCreateItem: false,
            canUpdateItem: false,
            canDeleteItem: false,

            // look up data
            expense_types: [],

            amount_sum: 0,

            // search
            search_panel: 0,
            date_from_menu: false,
            date_to_menu: false,
            search: {
                date_from: null,
                date_to: null,
                expense_type_id: null
            },
            search_filters: {},

            // datatable
            headers: [],
            total_items: null,
            items: [],
            loading: false,
            options: {
                page: null,
                itemsPerPage: null,
                sortBy: [],
                sortDesc: [],
                groupBy: [],
                groupDesc: [],
                multiSort: false,
                mustSort: false
            },

            item_id: null,

            // manage item
            show_item_dialog: false,
            item_dialog_header: null,
            item_dialog_loading: false,
            item_action: null,

            // delete item
            show_delete_item_dialog: false,
            delete_item_dialog_loading: false
        }),
        computed: {
            locale() {
                return this.$i18n.locale;
            },
            isSuperAdmin() {
                return this.$store.state.user_details.roles.map(obj => obj.name).indexOf('superadmin') > -1;
            }
        },
        created() {
            this.headers = this.getHeaders();
        },
        async mounted() {
            this.canCreateItem = await this.$store.dispatch('hasPermission', 'create_expense');
            this.canViewItem = await this.$store.dispatch('hasPermission', 'view_expense');
            this.canUpdateItem = await this.$store.dispatch('hasPermission', 'update_expense');
            this.canDeleteItem = await this.$store.dispatch('hasPermission', 'delete_expense');

            // criteria prop not set
            if (!this.criteria) {
                this.setDefaultSearchCriteria();
            }

            // get expense types
            let expense_types = await this.$store.dispatch('apiRequest', {
                method: 'get', url: 'settings/expense_types'
            });
            this.expense_types = this.$Helper.methods.sortArrayOfObjects({array: expense_types.data, property: 'name'});
        },
        watch: {
            locale(val) {
                this.headers = this.getHeaders();
            },
            search: {
                handler(val) {

                    let filters = this.$Helper.methods.createSearchFilters(Object.assign({}, val));

                    // if filters did change get data
                    if (JSON.stringify(this.filters) !== JSON.stringify(filters)) {
                        this.search_filters = filters;

                        setTimeout(() => {
                            this.getData();
                        }, 100);
                    }
                },
                deep: true
            },
            criteria(val) {
                this.items = [];
                this.search_filters = this.$Helper.methods.createSearchFilters(Object.assign({}, val));
                this.getData();
            }
        },
        methods: {
            // return headers array
            getHeaders() {
                return [
                    {text: `${this.$t('general.date')}`, value: 'date', sortable: true},
                    {text: `${this.$t('general.amount')}`, value: 'amount', sortable: true},
                    {text: `${this.$t('expenses.type')}`, value: 'expense_type_name', sortable: true},
                    {text: `${this.$t('datatable.actions')}`, value: 'actions', align: 'right', sortable: false}
                ]
            },

            // search
            // set default values for search form
            setDefaultSearchCriteria() {
                // set default search dates
                let date = new Date();
                this.search.date_to = date.toISOString().substring(0, 10);
                date.setDate(date.getDate() - 30);
                this.search.date_from = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
            },
            // clear search form
            clearSearchForm() {
                for (let prop in this.search) {
                    this.search[prop] = null;
                }

                this.setDefaultSearchCriteria();
            },

            // datatable
            // apply changed options on datatable
            async optionsChanged(options) {
                let options_changed = false;
                this.loading = true;

                // check if options changed
                for (let prop in this.options) {
                    if (this.options[prop] !== options[prop]) {
                        options_changed = true;
                        break;
                    }
                }

                if (options_changed) {
                    this.options = Object.assign({}, options);
                    await this.getData();
                }
            },
            // get data from API
            async getData() {
                this.loading = true;

                let response = await this.$store.dispatch('apiRequest', {
                    method: 'get', url: this.url, params: {
                        options: this.options, search_filters: this.search_filters
                    }
                });

                this.loading = false;

                // successful response
                if (!response.response) {
                    this.total_items = response.data.total_items;
                    this.items = response.data.items;
                    this.amount_sum = response.data.amount_sum;
                    this.$emit('data-changed', response.data);
                }
            },

            // Manage Item
            openItemDialog(action, id) {
                this.item_dialog_header = (action === 'create') ? this.$t('expenses.create')
                    : (action === 'update') ? this.$t('expenses.update') : this.$t('expenses.view');
                this.item_id = id;
                this.item_action = action;
                this.show_item_dialog = true;
            },
            // clear item dialog related data
            clearItemDialog() {
                this.item_dialog_header = null;
                this.item_id = null;
                this.item_action = null;
            },
            // close item dialog
            closeItemDialog() {
                this.show_item_dialog = false;
                this.clearItemDialog();
            },

            // submit item dialog
            submitItemDialog() {
                this.show_item_dialog = false;
                this.clearItemDialog();
                this.getData();
            },

            // Delete item
            // show delete item dialog
            showDeleteItemDialog(item) {
                this.show_delete_item_dialog = true;
                this.item_id = item.id;
            },
            // close delete item dialog
            closeDeleteItemDialog() {
                this.show_delete_item_dialog = false;
                this.item_id = null;
            },
            // delete item
            async deleteItem() {
                this.delete_item_dialog_loading = true;

                let response = await this.$store.dispatch('apiRequest', {
                    method: 'delete', url: `${this.url}/${this.item_id}`
                });

                this.delete_item_dialog_loading = false;

                // close dialog
                this.closeDeleteItemDialog();

                // successful response
                if (!response.response) {

                    // get items
                    await this.getData();
                }
            }
        }
    }
</script>